import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import { Eyebrow } from "@/components/Section";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { SEO } from "@/components/SEO";

export const Kontakt = () => {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [state, setState] = useState({ loading: false, sent: false, error: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, sent: false, error: "" });
    try {
      await api.post("/contact", form);
      setState({ loading: false, sent: true, error: "" });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : t.contact.error;
      setState({ loading: false, sent: false, error: msg });
    }
  };

  return (
    <div data-testid="kontakt-page" className="relative">
      <SEO
        title="Kontakt · IT-Beratung anfragen"
        description="Kontaktieren Sie IT-Servicepoint für eine kostenfreie Erstberatung: IT-Sicherheit, Managed Services, Backup und Server Hosting. Antwort innerhalb 24 Stunden."
        keywords="IT-Servicepoint Kontakt, IT-Beratung Hessen, Schenklengsfeld IT, Ron Kowalewsky, IT-Sicherheit Anfrage"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Kontakt – IT-Servicepoint",
          mainEntity: {
            "@type": "Organization",
            name: "IT-Servicepoint",
            telephone: "+49-6629-9153890",
            email: "info@it-servicepoint.eu",
          },
        }}
      />
      <section className="relative overflow-hidden bg-soft">
        <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28">
          <Eyebrow>Kontakt</Eyebrow>
          <h1 className="mt-6 font-display font-black text-5xl sm:text-7xl tracking-tightest leading-[0.95] text-ink max-w-3xl">
            {t.contact.title}
          </h1>
          <p className="mt-6 max-w-2xl text-muted text-lg">{t.contact.sub}</p>
        </div>
      </section>

      <section className="bg-white border-y border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8">
            <div>
              <Eyebrow>{t.contact.info_title}</Eyebrow>
              <h2 className="mt-4 font-display font-bold text-3xl text-ink">{t.contact.info_owner}</h2>
            </div>
            <ul className="space-y-5">
              {[
                { icon: MapPin, label: "Adresse", value: <span>{t.contact.info_address}</span> },
                { icon: Phone, label: "Telefon", value: <a className="hover:text-primary" href="tel:+4966299153890">+49 (0) 6629 9153890</a> },
                { icon: Mail, label: "E-Mail", value: <a className="hover:text-primary" href="mailto:info@it-servicepoint.eu">info@it-servicepoint.eu</a> },
                { icon: Clock, label: "Erreichbarkeit", value: <>Mo – Fr · 08:00 – 18:00<br/>24/7 Notfall-Support für Vertragskunden</> },
              ].map((it) => (
                <li key={it.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-soft border border-line flex items-center justify-center flex-shrink-0 text-primary">
                    <it.icon size={18} aria-hidden="true" />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">{it.label}</div>
                    <div className="text-ink mt-1">{it.value}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="border border-line p-6 bg-soft">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary">Was passiert nach Ihrer Anfrage?</div>
              <ol className="mt-4 space-y-3 text-sm text-ink">
                <li className="flex gap-3"><span className="font-mono text-accent">01.</span> Antwort innerhalb von 24h</li>
                <li className="flex gap-3"><span className="font-mono text-accent">02.</span> 30-Minuten-Beratungsgespräch</li>
                <li className="flex gap-3"><span className="font-mono text-accent">03.</span> Maßgeschneidertes Angebot</li>
              </ol>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="border border-line bg-white p-8 sm:p-10 space-y-5 shadow-soft"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label={t.contact.name}>
                  <input
                    required name="name" value={form.name} onChange={onChange}
                    data-testid="contact-input-name"
                    className="w-full bg-white border border-line px-4 py-3 focus:border-primary outline-none transition-colors"
                  />
                </Field>
                <Field label={t.contact.email}>
                  <input
                    required type="email" name="email" value={form.email} onChange={onChange}
                    data-testid="contact-input-email"
                    className="w-full bg-white border border-line px-4 py-3 focus:border-primary outline-none transition-colors"
                  />
                </Field>
                <Field label={t.contact.phone}>
                  <input
                    name="phone" value={form.phone} onChange={onChange}
                    data-testid="contact-input-phone"
                    className="w-full bg-white border border-line px-4 py-3 focus:border-primary outline-none transition-colors"
                  />
                </Field>
                <Field label={t.contact.subject}>
                  <input
                    name="subject" value={form.subject} onChange={onChange}
                    data-testid="contact-input-subject"
                    className="w-full bg-white border border-line px-4 py-3 focus:border-primary outline-none transition-colors"
                  />
                </Field>
              </div>
              <Field label={t.contact.message}>
                <textarea
                  required name="message" rows={6} value={form.message} onChange={onChange}
                  data-testid="contact-input-message"
                  className="w-full bg-white border border-line px-4 py-3 focus:border-primary outline-none transition-colors resize-none"
                />
              </Field>

              {state.sent && (
                <div data-testid="contact-success" className="border border-accent/50 bg-accent/10 p-4 flex items-center gap-3 text-ink">
                  <CheckCircle2 size={20} className="text-accent" /> {t.contact.sent}
                </div>
              )}
              {state.error && (
                <div data-testid="contact-error" className="border border-red-300 bg-red-50 p-4 text-red-700">
                  {state.error}
                </div>
              )}

              <button
                type="submit"
                disabled={state.loading}
                data-testid="contact-submit"
                className="group inline-flex items-center gap-2 px-7 py-3.5 bg-primary text-white font-medium hover:bg-primary-hover transition-colors disabled:opacity-50 shadow-soft"
              >
                {state.loading ? t.common.loading : t.contact.send}
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);
